import { Box } from '@chakra-ui/react';
import React, { FC, ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';
import AltTopNav from '../navigation/AltTopNav';

import { useAuth, useCMSManagementContext } from 'jaen';
import { useLocation } from '@reach/router';
import { useLocalizeHref, usePageLocale } from '../../contexts/locale';
import { MenuStructureContext } from '../../contexts/menu-structure';
import { createPageTree } from '../../utils/navigation';
import DocsLayout from './DocsLayout';
import Footer from '../sections/Footer';

interface AppLayoutProps {
  children?: React.ReactNode;
  isDocs?: boolean;
  path: string;
  footer?: FC<{ pullUp?: boolean }>;
}

/**
 * The global layout component.
 * This should not be directly used in pages, but used in gatsby.
 */
const AppLayout: FC<AppLayoutProps> = ({ children, isDocs, path, footer }) => {
  const cmsManager = useCMSManagementContext();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { locale, prefix } = usePageLocale();
  const localizeHref = useLocalizeHref();
  const intl = useIntl();

  // createPageTree is a plain util and cannot use react-intl, so the docs
  // section label is resolved here and threaded in like localizeHref.
  const docsSectionLabel = intl.formatMessage({
    id: 'DocsMenuSectionArticles',
    defaultMessage: 'Blog Artikel'
  });

  // This generates the menu structure from the page tree that is used over the
  // whole app by accessing the context. The locale prefix is stripped for
  // matching and the emitted hrefs are localized, so the docs menu works on
  // prefixed locales too.
  const menuStructure = useMemo(
    () =>
      createPageTree(
        cmsManager,
        location.pathname,
        prefix,
        localizeHref,
        docsSectionLabel
      ),
    [cmsManager, path, locale, prefix, docsSectionLabel]
  );

  const FooterComp: FC<{ pullUp?: boolean }> = footer ?? Footer;

  let childrenElmnt: ReactNode = null;

  if (isDocs) {
    childrenElmnt = <DocsLayout path={path}>{children}</DocsLayout>;
  } else {
    childrenElmnt = children;
  }

  return (
    <>
      <MenuStructureContext.Provider value={{ menuStructure }}>
      <Box
          as="main"
          minW="210px"
          h="max(100%, 100vh)"
          //bgImage={'url(/content/f3.png)'}
          minH="100vh">
          {!isAuthenticated && <AltTopNav path={path} />}
          {childrenElmnt}
        </Box>
      </MenuStructureContext.Provider>
      <FooterComp pullUp={path === '/'} />
    </>
  );
};

export default AppLayout;
